import { HttpErrorResponse } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MngLoadingService } from '@mng/mng-loading';
import { isDefined, looksLikeHtml, SERVER_ERROR_MESSAGE } from '@mng/mng-ts-helpers';
import { BehaviorSubject } from 'rxjs';
import { IMessage, IProgressInfo, MessageKind } from './message.types';
import { ApiResult } from '@mng/mng-types';

@Service()
export class MngMessageService {
  snackBar = inject(MatSnackBar);
  private loadingService = inject(MngLoadingService);

  defaultConfig: MatSnackBarConfig;
  errorConfig: MatSnackBarConfig;
  warningConfig: MatSnackBarConfig;
  subject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor() {
    this.defaultConfig = this.createDefaultSnackbarConfig();
    this.errorConfig = this.createDefaultSnackbarErrorConfig();
    this.warningConfig = this.createDefaultSnackbarWarningConfig();

    this.subject.subscribe((message: MessageKind) => {
      if (message) {
        if (message instanceof HttpErrorResponse) {
          if (this.isApiResult(message.error)) {
            this.handleApiResult(message.error);
          } else if (message.status === 401) {
            if (message.error?.message?.message) {
              void this.showMessage(message.error.message.message, this.errorConfig);
            } else {
              void this.showMessage('Aktion ist nicht erlaubt', this.errorConfig);
            }
          } else if (message.status === 202) {
          } else if (message.error?.message?.message) {
            if (message.error.message.type === 'warning') {
              void this.showMessage(message.error.message.message, this.warningConfig);
            } else {
              void this.showMessage(message.error.message.message, this.errorConfig);
            }
          } else if (message.error?.message && message.error.message !== 'No message available') {
            void this.showMessage(message.error.message, this.errorConfig);
          } else if (message.error && message.error.error) {
            void this.showMessage(message.error.error, this.errorConfig);
          } else if (message.error) {
            if (typeof message.error === 'string') {
              if (looksLikeHtml(message.error)) {
                void this.showMessage(SERVER_ERROR_MESSAGE, this.errorConfig);
              } else {
                void this.showMessage(message.error, this.errorConfig);
              }
            } else if (Object.keys(message.error).length > 0) {
              void this.showMessage(JSON.stringify(message.error), this.errorConfig);
            } else {
              void this.showMessage('Ein Systemfehler ist aufgetreten', this.errorConfig);
            }
          } else {
            void this.showMessage('Ein Systemfehler ist aufgetreten', this.errorConfig);
          }
        } else if (this.isApiResult(message)) {
          this.handleApiResult(message);
        } else if (typeof message === 'string') {
          void this.showMessage(message);
        } else if ((<IMessage>message).message) {
          if ((<IMessage>message).type === 'error') {
            void this.showMessage((<IMessage>message).message, this.errorConfig);
          } else {
            void this.showMessage((<IMessage>message).message);
          }
        } else if (isDefined((<IProgressInfo>message).inProgress)) {
          if ((<IProgressInfo>message).inProgress) {
            this.loadingService.showOverlay();
          } else {
            this.loadingService.removeOverlay();
          }
        } else {
          void this.showMessage('Ein Systemfehler ist aufgetreten', this.errorConfig);
        }
      }
    });
  }

  private isApiResult(message: any): message is ApiResult<any, any> {
    return message && message.success !== undefined && message.errors !== undefined;
  }

  private handleApiResult(message: ApiResult<any, any>): void {
    if (message.success === false) {
      if (message.errors && message.errors.length > 0) {
        message.errors.forEach((error) => {
          const errorDetails = error.details;
          if (typeof errorDetails === 'string') {
            void this.showMessage(errorDetails, this.errorConfig);
          } else if (Array.isArray(errorDetails)) {
            errorDetails.forEach((detail) => {
              if (typeof detail === 'string') {
                void this.showMessage(detail, this.errorConfig);
              } else {
                void this.showMessage(JSON.stringify(detail), this.errorConfig);
              }
            });
          } else if (typeof errorDetails === 'object' && errorDetails !== null) {
            void this.showMessage(JSON.stringify(errorDetails), this.errorConfig);
          } else {
            void this.showMessage(String(errorDetails), this.errorConfig);
          }
        });
      } else {
        void this.showMessage('Ein Fehler ist aufgetreten (keine Details verfügbar)', this.errorConfig);
      }
    }
  }

  pushValue(value: MessageKind): void {
    this.subject.next(value);
  }

  async showMessage(
    message: string,
    config: MatSnackBarConfig = this.defaultConfig,
    action?: () => void,
    actionLabel = 'OK'
  ): Promise<void> {
    await this.snackBar
      .open(message, action ? actionLabel : '', config)
      .onAction()
      .toPromise();

    if (action) {
      action();
    }
  }

  async showError(message: string, action?: () => void, actionLabel = 'OK'): Promise<void> {
    return void this.showMessage(message, this.errorConfig, action, actionLabel);
  }

  async showWarning(message: string, action?: () => void, actionLabel = 'OK'): Promise<void> {
    return void this.showMessage(message, this.warningConfig, action, actionLabel);
  }

  createDefaultSnackbarConfig(duration?: number): MatSnackBarConfig {
    const config = new MatSnackBarConfig();
    config.duration = duration || 5000;
    config.panelClass = ['default-snackbar'];
    return config;
  }

  createDefaultSnackbarTopRightConfig(duration?: number): MatSnackBarConfig {
    const config = this.createDefaultSnackbarConfig(duration);
    config.verticalPosition = 'top';
    config.horizontalPosition = 'right';

    return config;
  }

  createDefaultSnackbarErrorConfig(duration = 10000): MatSnackBarConfig {
    const config = this.createDefaultSnackbarConfig(duration);
    config.panelClass = ['error-snackbar'];
    return config;
  }

  createDefaultSnackbarWarningConfig(duration = 10000): MatSnackBarConfig {
    const config = this.createDefaultSnackbarConfig(duration);
    config.panelClass = ['warning-snackbar'];
    return config;
  }
}
